import { Controller } from "@hotwired/stimulus"

// Preserve the sidebar's scroll position across Turbo navigations.
//
// The whole <header id="navigation-offcanvas"> re-renders on every visit — it is
// intentionally NOT data-turbo-permanent, so the server-rendered `active`
// highlight stays correct — which recreates the scrollable menu region fresh and
// resets its scrollTop to 0. For a menu item below the fold (e.g. "Príkazy na
// úhradu") that meant the menu visibly jumped back to the top after each click.
//
// We snapshot the scroll offset before the old header leaves the DOM
// (turbo:before-render, while it is still laid out) and restore it once the new
// header connects. Keyed by menu identity so switching context
// (app ↔ settings ↔ finance-management) never restores a position from a
// different menu. Module-level so it survives the disconnect/connect cycle.
const savedMenuScroll = new Map()

// Test helper: clears the persisted scroll positions between test cases.
export function resetNavMenuScroll() {
  savedMenuScroll.clear()
}

// Connects to data-controller="navigation"
export default class extends Controller {
  connect() {
    this.#toggleOffcanvas()
    // remove lucanto-header--hidden class after initial load, without it the offcanvas could be visible for a short time
    this.element.classList.remove("lucanto-header--hidden")
    this.#restoreMenuScroll()

    window.addEventListener("resize", this.#handleResize)
    document.addEventListener("turbo:frame-missing", this.#handleTurboFrameMissing)
    document.addEventListener("turbo:before-render", this.#saveMenuScroll)
  }

  disconnect() {
    window.removeEventListener("resize", this.#handleResize)
    document.removeEventListener("turbo:frame-missing", this.#handleTurboFrameMissing)
    document.removeEventListener("turbo:before-render", this.#saveMenuScroll)
  }

  #toggleOffcanvas = () => {
    if (this.#isMobileDevice()) {
      this.element.classList.add("offcanvas", "offcanvas-start")
    } else {
      this.element.classList.remove("offcanvas", "offcanvas-start")
    }
  }

  #isMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera
    const isMobileUserAgent = /android/i.test(userAgent) || (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream)
    const isMobileWindowSize = window.innerWidth <= 992

    return isMobileUserAgent || isMobileWindowSize
  }

  #handleTurboFrameMissing(event) {
    const {
      detail: { response, visit }
    } = event
    event.preventDefault()
    visit(response.url)
  }

  #handleResize = () => {
    this.#toggleOffcanvas()
  }

  // The actually-overflowing scroll container inside the sidebar. Which element
  // scrolls depends on the rendered menu (main / workspace-settings / finance),
  // so pick the first candidate whose content overflows.
  #menuScroller = () => {
    const candidates = this.element.querySelectorAll(
      ".navigation--stretch, .navigation--stretch-workspace, .navigation__wrapper"
    )
    for (const el of candidates) {
      if (el.scrollHeight > el.clientHeight) return el
    }
    return candidates[0] || null
  }

  // Stable per menu-context, distinct across contexts: the first nav link's path
  // (dashboard for the app menu, a settings route for settings, etc.).
  #menuKey = (scroller) => {
    const firstLink = scroller.querySelector("a[href]")
    return firstLink ? new URL(firstLink.href, window.location.origin).pathname : scroller.className
  }

  #saveMenuScroll = () => {
    const scroller = this.#menuScroller()
    if (!scroller) return
    savedMenuScroll.set(this.#menuKey(scroller), scroller.scrollTop)
  }

  #restoreMenuScroll = () => {
    const scroller = this.#menuScroller()
    if (!scroller) return
    const key = this.#menuKey(scroller)
    if (savedMenuScroll.has(key)) scroller.scrollTop = savedMenuScroll.get(key)
  }
}
